import { useEffect, useState, useRef } from "react";
import { Box, Paper, Typography, Stack, CircularProgress, useTheme } from "@mui/material";
import apiService from "../../../service/Api";
import { Image } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";

interface VenueLayouts {
  capacity: number;
  id: number;
}

interface VenueDocument {
  id: number;
  file: string;
  file_type: string;
  venue_id?: number;
  venue?: number;
}

interface Venue {
  id: number;
  name: string;
  address_one: string | null;
  venue_type: string | null;
  completeness_score: number | null;
  capacity: string | null;
  layouts: VenueLayouts[];
  power_type: string | null;
  internet_service_provider: string | null;
  files: VenueDocument[];
}

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Venue[];
}

export default function VenuesWidget() {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await apiService.get<ApiResponse>(
          "/api/venues/venue/",
          {
            page: 1,
            page_size: 1000,
          },
        );

        const sorted = (response.data?.results || []).sort(
          (a, b) => (a.completeness_score ?? 0) - (b.completeness_score ?? 0),
        );

        setVenues(sorted.slice(0, 10));
      } catch (error) {
        console.error("Failed to fetch venues", error);
        setVenues([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, []);

  const getVenueImageUrl = (venue?: Venue) => {
    if (!venue?.files?.length) return null;
    const image =
      venue.files.find((file) => file.file_type === "venue_photos") ||
      venue.files[0];
    return image?.file || null;
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const width = scrollContainerRef.current.clientWidth;
      const newIndex = Math.round(scrollLeft / width);
      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    }
  };

  const scrollTo = (index: number) => {
    if (scrollContainerRef.current) {
      const width = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: width * index,
        behavior: "smooth",
      });
      setActiveIndex(index);
    }
  };

  const slide1 = venues.slice(0, 4);
  const slide2 = venues.slice(4, 8);
  const slide3 = venues.slice(8, 10);

  const featuredVenue = slide1[0];
  const featuredVenueImage = getVenueImageUrl(featuredVenue);

  const dots = [];
  if (slide1.length > 0) dots.push(0);
  if (slide2.length > 0) dots.push(1);
  if (slide3.length > 0) dots.push(2);

  const renderListItem = (venue: Venue, rank: number) => {
    const percent = Math.round(venue.completeness_score ?? 0);
    return (
      <Box
        key={venue.id}
        onClick={() => navigate(`/venues/view/${venue.id}`)}
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${colors.primary[800]}`,
          borderRadius: "16px",
          p: 1,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: colors.primary[800],
            transform: "translateY(-2px)",
          },
        }}
      >
        <Typography
          fontWeight={800}
          color={colors.primary[400]}
          sx={{ width: 40, textAlign: "center", fontSize: "2.2rem" }}
        >
          {rank}
        </Typography>

        <Box sx={{ flexGrow: 1, ml: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} color={colors.primary[700]} noWrap>
            {venue.name}
          </Typography>
          <Typography variant="caption" color={colors.primary[300]} noWrap display="block">
            {venue.address_one || "No address"}
          </Typography>
        </Box>

        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            variant="determinate"
            value={100}
            sx={{ color: colors.primary[800] }}
            size={40}
            thickness={4}
          />
          <CircularProgress
            variant="determinate"
            value={percent}
            sx={{ color: "#F57C00", position: "absolute", left: 0 }}
            size={40}
            thickness={4}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              component="div"
              color={colors.primary[700]}
              sx={{ fontSize: "0.6rem", fontWeight: "bold" }}
            >
              {percent}%
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: 2,
        height: "100%",
        backgroundColor: colors.primary[900],
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="subtitle1"
        fontWeight={800}
        color={colors.primary[700]}
        sx={{ display: "block", mb: 1 }}
      >
        VENUES NEEDING ATTENTION
      </Typography>

      {loading ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={26} sx={{ color: colors.primary[400] }} />
        </Box>
      ) : venues.length === 0 ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color={colors.primary[300]}>
            No venues found
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              display: "flex",
              flexGrow: 1,
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              sx={{
                minWidth: "100%",
                scrollSnapAlign: "start",
                display: "flex",
                flexDirection: "column",
                pr: slide2.length > 0 ? 2 : 0,
              }}
            >
              {featuredVenue && (
                <Box
                  onClick={() => navigate(`/venues/view/${featuredVenue.id}`)}
                  sx={{
                    position: "relative",
                    borderRadius: "16px",
                    overflow: "hidden",
                    height: 120,
                    mb: 2,
                    color: colors.primary[900],
                    cursor: "pointer",
                    transition: "transform 0.3s ease",
                    "&:hover": { transform: "scale(1.02)" },
                  }}
                >
                  {featuredVenueImage ? (
                    <Box
                      component="img"
                      src={featuredVenueImage}
                      alt={featuredVenue.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[400]} 100%)`,
                        color: "white",
                      }}
                    >
                      <Box textAlign="center">
                        <Image sx={{ fontSize: 40, opacity: 0.7 }} />
                      </Box>
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(to right, rgba(0,0,0,0.75), rgba(0,0,0,0.25))",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="caption">
                        {featuredVenue.venue_type || "Venue"}
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: "#F57C00",
                          borderRadius: "50%",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          {Math.round(featuredVenue.completeness_score ?? 0)}%
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {featuredVenue.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {featuredVenue.address_one || "No address"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Stack spacing={2}>
                {slide1
                  .slice(1)
                  .map((venue, index) => renderListItem(venue, index + 2))}
              </Stack>
            </Box>

            {slide2.length > 0 && (
              <Box
                sx={{
                  minWidth: "100%",
                  scrollSnapAlign: "start",
                  display: "flex",
                  flexDirection: "column",
                  pr: slide3.length > 0 ? 2 : 0,
                }}
              >
                <Stack spacing={2}>
                  {slide2.map((venue, index) =>
                    renderListItem(venue, index + 5),
                  )}
                </Stack>
              </Box>
            )}

            {slide3.length > 0 && (
              <Box
                sx={{
                  minWidth: "100%",
                  scrollSnapAlign: "start",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack spacing={2}>
                  {slide3.map((venue, index) =>
                    renderListItem(venue, index + 9),
                  )}
                </Stack>
              </Box>
            )}
          </Box>

          {dots.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                gap: 0.5,
              }}
            >
              {dots.map((index) => (
                <Box
                  key={index}
                  onClick={() => scrollTo(index)}
                  sx={{
                    width: activeIndex === index ? 16 : 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor:
                      activeIndex === index ? colors.primary[400] : colors.primary[50],
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}